import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type {
    PendingTicketFormData,
    SearchSimilarTicketsResult,
    SimilarTicket,
} from "@/domain/entities/vistassistant/TicketTypes";
import { TicketType } from "@/domain/entities/vistassistant/TicketTypes";
import { useTranslation } from "react-i18next";
import ConversationBubble from "./ConversationBubble";

type SearchSimilarTicketsResponseProps = {
    data: SearchSimilarTicketsResult;
    onConfirmCreate: (
        pendingData: PendingTicketFormData,
        ticketType: TicketType,
    ) => void;
};

const SearchSimilarTicketsResponse = ({
    data,
    onConfirmCreate,
}: SearchSimilarTicketsResponseProps) => {
    const { t } = useTranslation();
    const hasSimilar = data.tickets.length > 0;

    return (
        <ConversationBubble>
            <div className="flex flex-col gap-3 text-sm">
                {hasSimilar && (
                    <>
                        <p className="font-medium text-yellow-700 dark:text-yellow-300">
                            {t("vistassistant.searchSimilarTickets.found", {
                                count: data.tickets.length,
                            })}
                        </p>
                        <div className="flex flex-col gap-2">
                            {data.tickets.map((ticket: SimilarTicket) => (
                                <div
                                    key={ticket.id}
                                    className="rounded border bg-muted p-2"
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Badge className="border border-border bg-transparent text-foreground">
                                            {ticket.type}
                                        </Badge>
                                        {ticket.feature && (
                                            <Badge className="border border-border bg-transparent text-foreground">
                                                {ticket.feature}
                                            </Badge>
                                        )}
                                        {ticket.votes !== undefined && (
                                            <span className="text-xs text-muted-foreground">
                                                {ticket.votes} votes
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-1">{ticket.title}</p>
                                    {ticket.description && (
                                        <p className="text-xs text-muted-foreground">
                                            {ticket.description}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <Button
                    className="w-fit"
                    onClick={() =>
                        onConfirmCreate(data.pendingFormData, data.ticketType)
                    }
                    size="sm"
                >
                    {hasSimilar
                        ? t("vistassistant.searchSimilarTickets.createAnyway")
                        : t("vistassistant.searchSimilarTickets.create")}
                </Button>
            </div>
        </ConversationBubble>
    );
};

export default SearchSimilarTicketsResponse;
